import { Shield, ArrowLeft } from "lucide-react";

const ORG_NAME = "Rolling River Day Camp";
const EFFECTIVE_DATE = "February 2026";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50" data-testid="privacy-policy-page">
      <header className="bg-emerald-900 text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg sm:text-xl font-bold">{ORG_NAME}</h1>
            <p className="text-xs text-emerald-200">Privacy Policy</p>
          </div>
          <a href="/login" className="text-xs bg-emerald-700 hover:bg-emerald-600 px-3 py-1.5 rounded-lg font-medium inline-flex items-center gap-1" data-testid="back-to-app">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to App
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div className="bg-white rounded-2xl border-2 border-emerald-200 p-6 sm:p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Privacy Policy</h2>
              <p className="text-sm text-gray-500">Effective Date: {EFFECTIVE_DATE}</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 mt-2">This Privacy Policy describes how {ORG_NAME} collects, uses, and protects the personal information of parents and guardians (“you”) who enroll a child at our camp and receive communications from us.</p>
        </div>

        <div className="bg-white rounded-2xl border p-6 sm:p-8 shadow-sm space-y-6 text-sm text-gray-700 leading-relaxed">
          <section>
            <h3 className="text-base font-bold text-gray-900 mb-2">1. Information We Collect</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Contact information</strong> provided during camp enrollment (CampMinder): parent/guardian name, email, mobile phone number, home address, emergency contacts.</li>
              <li><strong>Camper information</strong> provided during enrollment: camper name, age, grade, group assignment, medical/allergy flags, photo.</li>
              <li><strong>Operational data</strong> generated during camp: daily attendance, incident reports, swim level, lunch orders, bus stop assignments.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-base font-bold text-gray-900 mb-2">2. How We Use Your Information</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>To operate the camp safely — attendance, medical needs, bus transportation, meal service.</li>
              <li>To communicate with you about your child — daily updates, reminders, early dismissal, late arrival, incident follow-up, weather closures.</li>
              <li>To send SMS text messages and emails you have consented to receive at enrollment. See our <a href="/sms-terms" className="underline text-emerald-700 font-semibold">SMS Terms &amp; Consent</a>.</li>
            </ul>
          </section>

          <section data-testid="mobile-number-disclosure">
            <h3 className="text-base font-bold text-gray-900 mb-2">3. Mobile Phone Numbers — No Third-Party Sharing</h3>
            <p><strong>Mobile information and phone numbers are not shared with any third parties or affiliates for marketing or promotional purposes.</strong> Your number is used only by {ORG_NAME} and our service providers (Twilio for SMS delivery, Resend for email delivery) strictly to send the camp-related messages described in our SMS Terms. No data is sold, rented, or transferred to advertisers, data brokers, or unrelated third parties. Service providers are contractually bound to process data only on our behalf and to protect it.</p>
          </section>

          <section>
            <h3 className="text-base font-bold text-gray-900 mb-2">4. Message Frequency &amp; Carrier Charges</h3>
            <p>Message frequency varies based on camp activities and operational needs, and will not exceed approximately <strong>10 messages per week</strong> under normal circumstances. <strong>Message and data rates may apply</strong> depending on your mobile carrier and plan. {ORG_NAME} is not responsible for charges imposed by your carrier.</p>
          </section>

          <section>
            <h3 className="text-base font-bold text-gray-900 mb-2">5. Your Consent &amp; How to Opt Out</h3>
            <p>Consent to receive SMS is collected during camp enrollment through our registration platform (CampMinder). SMS consent is <strong>not a condition</strong> of enrolling your child — email communication is always available as an alternative. You can stop receiving text messages at any time by replying <strong>STOP</strong> to any message we send. Reply <strong>HELP</strong> for assistance.</p>
          </section>

          <section>
            <h3 className="text-base font-bold text-gray-900 mb-2">6. Data Retention</h3>
            <p>Camper and contact information is retained for the duration of enrollment and for a reasonable period afterward as required for legal, tax, and medical record obligations. Operational logs (attendance, incidents) are retained in accordance with applicable state law.</p>
          </section>

          <section>
            <h3 className="text-base font-bold text-gray-900 mb-2">7. Security</h3>
            <p>We use industry-standard safeguards to protect your information, including encryption in transit (HTTPS) and access controls on internal systems. No system is 100% secure; if you believe your information has been compromised, contact the camp office immediately.</p>
          </section>

          <section>
            <h3 className="text-base font-bold text-gray-900 mb-2">8. Children's Privacy</h3>
            <p>Camper information is collected from a parent or guardian during enrollment. {ORG_NAME} does not knowingly collect information directly from children under 13 online.</p>
          </section>

          <section>
            <h3 className="text-base font-bold text-gray-900 mb-2">9. Contact Us</h3>
            <p>Questions about this Privacy Policy? Contact the camp office directly. See the contact details listed on the camp's main enrollment page.</p>
          </section>
        </div>

        <footer className="text-center pb-8">
          <p className="text-xs text-gray-400">{ORG_NAME}</p>
          <p className="text-xs text-gray-400 mt-1">See also: <a href="/sms-terms" className="underline">SMS Terms &amp; Consent</a></p>
        </footer>
      </main>
    </div>
  );
}
